// Tremor Input [v2.0.0]
import React from 'react'
import { IconEye, IconEyeOff, IconSearch } from '@/components/icons'
import { tv, type VariantProps } from 'tailwind-variants'
import { cx, focusInput, focusRing, hasErrorInput } from '@/lib/utils'

const inputStyles = tv({
  base: [
    'relative block w-full appearance-none rounded-md border px-2.5 py-2 shadow-xs outline-hidden transition sm:text-sm',
    'border-gray-300',
    'text-gray-900',
    'placeholder-gray-400',
    'bg-white',
    'disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
    ...focusInput,
    '[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden',
  ],
  variants: {
    hasError: {
      true: hasErrorInput,
    },
    enableStepper: {
      false:
        '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
    },
  },
})

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof inputStyles> {
  inputClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, inputClassName, hasError, enableStepper = true, type, ...props }: InputProps, forwardedRef) => {
    const [typeState, setTypeState] = React.useState(type)

    const isPassword = type === 'password'
    const isSearch = type === 'search'

    return (
      <div className={cx('relative w-full', className)}>
        <input
          ref={forwardedRef}
          type={isPassword ? typeState : type}
          className={cx(
            inputStyles({ hasError, enableStepper }),
            {
              'pl-8': isSearch,
              'pr-10': isPassword,
            },
            inputClassName,
          )}
          {...props}
        />
        {isSearch && (
          <div className="pointer-events-none absolute bottom-0 left-2 flex h-full items-center justify-center text-gray-400">
            <IconSearch className="size-[1.125rem] shrink-0" aria-hidden="true" />
          </div>
        )}
        {isPassword && (
          <div className="absolute bottom-0 right-0 flex h-full items-center justify-center px-3">
            <button
              aria-label="Change password visibility"
              className={cx(
                'h-fit w-fit rounded-xs outline-hidden transition-all text-gray-400 hover:text-gray-500',
                focusRing,
              )}
              type="button"
              onClick={() => {
                setTypeState(typeState === 'password' ? 'text' : 'password')
              }}
            >
              <span className="sr-only">{typeState === 'password' ? 'Show password' : 'Hide password'}</span>
              {typeState === 'password' ? (
                <IconEye aria-hidden="true" className="size-5 shrink-0" />
              ) : (
                <IconEyeOff aria-hidden="true" className="size-5 shrink-0" />
              )}
            </button>
          </div>
        )}
      </div>
    )
  },
)

Input.displayName = 'Input'

export { Input, inputStyles, type InputProps }
